import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Zap, Shield, Cpu, Flame, Video, Radio, Droplet,
  ArrowRight, Check, Activity, Search, Shield as ShieldIcon, ClipboardCheck,
} from "lucide-react";
import SectionHeader from "../components/SectionHeader";

const HERO_BG = "https://images.pexels.com/photos/35573433/pexels-photo-35573433.jpeg";
const INSTACLEAN_IMG = "https://images.pexels.com/photos/28824108/pexels-photo-28824108.jpeg";

const PILLAR_IMAGES = {
  electrical: "https://images.pexels.com/photos/28824108/pexels-photo-28824108.jpeg",
  iot: "https://images.pexels.com/photos/586020/pexels-photo-586020.jpeg",
  ai_video: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg",
  fire: "https://images.pexels.com/photos/266526/pexels-photo-266526.jpeg",
  pipe: "https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg",
  rf: "https://images.pexels.com/photos/1774389/pexels-photo-1774389.jpeg",
  eo: "https://images.pexels.com/photos/1714208/pexels-photo-1714208.jpeg",
};

const PILLARS = [
  { icon: Zap, image: PILLAR_IMAGES.electrical, title: "Electrical Cleaning", desc: "POWER-ON deep cleaning for HT/LT panels, switchgear and rotating equipment up to 50kV.", testid: "pillar-electrical" },
  { icon: Cpu, image: PILLAR_IMAGES.iot, title: "IoT Monitoring", desc: "Edge sensors + gateways delivering thermal, current-signature and vibration telemetry.", testid: "pillar-iot" },
  { icon: Video, image: PILLAR_IMAGES.ai_video, title: "AI Training Videos", desc: "Generative training content for safety, SOPs and maintenance — in minutes, not months.", testid: "pillar-ai-video" },
  { icon: Flame, image: PILLAR_IMAGES.fire, title: "Fire Prevention", desc: "Engineered paint schemes, intumescent coatings and active non-flammable agents.", testid: "pillar-fire" },
  { icon: Shield, image: PILLAR_IMAGES.pipe, title: "Hot-Pipe Insulation Cleaning", desc: "Non-destructive cleaning systems for insulated steam, process and refinery pipework.", testid: "pillar-hot-pipe" },
  { icon: Radio, image: PILLAR_IMAGES.rf, title: "RF Solutions", desc: "Custom RF modules for asset tracking, remote actuation and hazardous-zone telemetry.", testid: "pillar-rf" },
  { icon: Droplet, image: PILLAR_IMAGES.eo, title: "EO Solutions", desc: "Electro-optical sensing & imaging for thermal mapping, spark detection and condition inference.", testid: "pillar-eo" },
];

const METHOD_IMAGES = {
  diagnose: "https://images.pexels.com/photos/159298/gears-cogs-machine-machinery-159298.jpeg",
  execute: "https://images.pexels.com/photos/28824108/pexels-photo-28824108.jpeg",
  contain: "https://images.pexels.com/photos/8487792/pexels-photo-8487792.jpeg",
  verify: "https://images.pexels.com/photos/3862601/pexels-photo-3862601.jpeg",
};

const METHOD = [
  { step: "01", icon: Search, image: METHOD_IMAGES.diagnose, title: "Diagnose", desc: "Thermography, acoustic scan and load signature baseline the risk surface." },
  { step: "02", icon: Activity, image: METHOD_IMAGES.execute, title: "Execute", desc: "Controlled 0.8-bar spray of non-conductive formulation under live load." },
  { step: "03", icon: ShieldIcon, image: METHOD_IMAGES.contain, title: "Contain", desc: "Enclosed capture of flushed contaminants — zero residue, zero ambient impact." },
  { step: "04", icon: ClipboardCheck, image: METHOD_IMAGES.verify, title: "Verify", desc: "Post-clean thermal delta and insulation-resistance confirmation." },
];

const INDUSTRY_IMGS = {
  power: "https://images.pexels.com/photos/14319099/pexels-photo-14319099.jpeg",
  data: "https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg",
  defense: "https://images.pexels.com/photos/20875971/pexels-photo-20875971.jpeg",
  mining: "https://images.pexels.com/photos/4993793/pexels-photo-4993793.jpeg",
};

export default function HomePage() {
  const { t } = useTranslation();
  const trust = t("trust.items", { returnObjects: true }) || [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-800">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/50" />
        <div className="absolute inset-0 grain" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl fade-up">
            <div className="font-mono-spec text-xs text-amber-400 mb-6" data-testid="hero-kicker">
              {t("hero.kicker")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-[1.02] tracking-tight">
              {t("hero.title_line1")}
              <br />
              <span className="text-amber-400">{t("hero.title_line2")}</span>
            </h1>
            <p className="mt-6 text-slate-300 text-lg max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/demo"
                data-testid="hero-primary-cta"
                className="inline-flex items-center gap-2 bg-amber-500 text-slate-950 font-bold px-6 py-3 hover:bg-amber-400 transition-colors rounded-sm glow-gold"
              >
                {t("hero.primary_cta")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/products"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 border border-blue-500 text-blue-300 font-semibold px-6 py-3 hover:bg-blue-950/40 transition-colors rounded-sm"
              >
                {t("hero.secondary_cta")}
              </Link>
            </div>

            {/* stat strip */}
            <div className="mt-14 grid grid-cols-3 gap-8 max-w-xl">
              <div>
                <div className="font-display text-3xl font-extrabold text-white">50kV</div>
                <div className="font-mono-spec text-[11px] text-slate-400 mt-1">POWER-ON CLEAN</div>
              </div>
              <div>
                <div className="font-display text-3xl font-extrabold text-white">20 yrs</div>
                <div className="font-mono-spec text-[11px] text-slate-400 mt-1">ZERO INCIDENTS</div>
              </div>
              <div>
                <div className="font-display text-3xl font-extrabold text-white">3</div>
                <div className="font-mono-spec text-[11px] text-slate-400 mt-1">CONTINENTS</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST TICKER */}
      <section data-testid="trust-ticker" className="bg-slate-900 border-b border-slate-800 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-4">
          {[...trust, ...trust].map((item, i) => (
            <div key={i} className="flex items-center mx-8 font-mono-spec text-xs text-slate-400">
              <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full mr-4" />
              {item}
            </div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 lg:py-32 border-b border-slate-800" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            kicker={t("home.pillars_kicker")}
            title={t("home.pillars_title")}
            description={t("home.pillars_desc")}
            testIdPrefix="pillars"
          />
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {PILLARS.map((p, i) => (
              <article
                key={p.title}
                data-testid={p.testid}
                className="group relative overflow-hidden border border-slate-800 bg-slate-950 min-h-[340px] flex flex-col hover:border-amber-500/60 transition-colors"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-25 group-hover:opacity-45 group-hover:scale-110 transition-all duration-700"
                  style={{ backgroundImage: `url(${p.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/90 to-slate-950/30" />
                <div className="relative p-6 flex flex-col h-full">
                  <div className="flex items-center justify-between">
                    <div className="w-11 h-11 flex items-center justify-center bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-sm group-hover:bg-amber-500 group-hover:text-slate-950 transition-colors">
                      <p.icon size={18} />
                    </div>
                    <div className="font-mono-spec text-[10px] text-amber-400/80">0{i + 1}</div>
                  </div>
                  <div className="mt-auto pt-16">
                    <h3 className="font-display text-xl font-bold text-white leading-tight">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{p.desc}</p>
                    <div className="mt-5 h-px bg-gradient-to-r from-amber-500/60 via-amber-500/20 to-transparent" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="py-24 lg:py-32 relative overflow-hidden border-b border-slate-800" data-testid="methodology-section">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: `url(${INSTACLEAN_IMG})` }} />
        <div className="absolute inset-0 bg-slate-950/90" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            kicker={t("home.methodology_kicker")}
            title={t("home.methodology_title")}
            description={t("home.methodology_desc")}
            testIdPrefix="method"
          />

          {/* Timeline connector */}
          <div className="mt-16 relative">
            <div className="hidden lg:block absolute top-[88px] left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {METHOD.map((m, i) => (
                <div
                  key={m.step}
                  data-testid={`method-step-${i + 1}`}
                  className="group relative"
                >
                  {/* image tile */}
                  <div className="relative aspect-[4/3] overflow-hidden border border-slate-800 bg-slate-900">
                    <img
                      src={m.image}
                      alt={m.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
                    {/* big step number */}
                    <div className="absolute top-4 left-4">
                      <div className="font-display font-extrabold text-5xl lg:text-6xl text-amber-400 leading-none">
                        {m.step}
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 w-12 h-12 flex items-center justify-center bg-slate-950/80 border border-amber-500/50 backdrop-blur-sm">
                      <m.icon className="text-amber-400" size={20} />
                    </div>
                  </div>
                  {/* text block */}
                  <div className="mt-5 px-1">
                    <div className="font-mono-spec text-[10px] text-amber-400 mb-2">
                      STEP {m.step} / 04
                    </div>
                    <h3 className="font-display text-2xl font-bold text-white leading-tight">{m.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                  </div>
                  {/* connector dot on large screens */}
                  <div className="hidden lg:block absolute top-[84px] -right-3 w-2 h-2 rounded-full bg-amber-400 ring-4 ring-slate-950" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 lg:py-32 border-b border-slate-800" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader kicker="WHERE WE WORK" title={t("industries.title")} description={t("industries.subtitle")} testIdPrefix="industries" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { img: INDUSTRY_IMGS.power, title: "Power & Energy", desc: "Substations, grids, inverters." },
              { img: INDUSTRY_IMGS.data, title: "Data Centers", desc: "UPS, server rooms, cable trays." },
              { img: INDUSTRY_IMGS.defense, title: "Defense", desc: "Submarines, secure sites." },
              { img: INDUSTRY_IMGS.mining, title: "Mining & Heavy", desc: "Underground & surface ops." },
            ].map((x, i) => (
              <div key={x.title} data-testid={`industry-card-${i}`} className="group relative border border-slate-800 overflow-hidden bg-slate-900">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={x.img} alt={x.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-display font-bold text-white text-lg">{x.title}</h3>
                  <p className="text-xs text-slate-300 mt-1">{x.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden" data-testid="home-cta">
        <div className="absolute inset-0 grain opacity-60" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
          <div className="border border-amber-500/40 p-10 lg:p-14 bg-slate-950">
            <div className="font-mono-spec text-xs text-amber-400 mb-4">{t("hero.kicker")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
              {t("home.cta_title")}
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed">{t("home.cta_desc")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/demo"
                data-testid="home-cta-primary"
                className="inline-flex items-center gap-2 bg-amber-500 text-slate-950 font-bold px-6 py-3 hover:bg-amber-400 transition-colors rounded-sm glow-gold"
              >
                {t("nav.demo")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="home-cta-secondary"
                className="inline-flex items-center gap-2 border border-slate-700 text-slate-200 px-6 py-3 hover:border-white transition-colors rounded-sm"
              >
                {t("nav.contact")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
